import React from 'react';
import { Shield, Target, Eye, Users, Award, Globe, CheckCircle } from 'lucide-react';
import { Card } from '@components/common/Card';

export const About: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-ncaa-primary to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">About NCAA</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Nigeria Civil Aviation Authority - Ensuring Safety and Excellence in Aviation
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Introduction */}
        <Card className="mb-8">
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-ncaa-dark mb-4">Who We Are</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              The Nigeria Civil Aviation Authority (NCAA) is the apex regulatory body for civil
              aviation in Nigeria. Established to oversee and regulate the aviation industry, we are
              committed to ensuring the highest standards of safety, security, and efficiency in
              Nigerian airspace.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Our Digital Civil Aviation Enrollment (DCEV) Portal represents our commitment to
              modernizing aviation services, making licensing and verification processes more
              accessible, transparent, and efficient for all stakeholders.
            </p>
          </div>
        </Card>

        {/* Mission, Vision, Values */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <Target className="h-8 w-8 text-ncaa-primary" />
              </div>
              <h3 className="text-xl font-bold text-ncaa-dark mb-3">Our Mission</h3>
              <p className="text-gray-600">
                To provide safe, secure, and efficient air transportation through effective
                regulation, oversight, and enforcement of civil aviation standards.
              </p>
            </div>
          </Card>

          <Card>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <Eye className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-ncaa-dark mb-3">Our Vision</h3>
              <p className="text-gray-600">
                To be a world-class aviation regulatory authority, recognized for excellence in
                safety oversight and service delivery in the aviation industry.
              </p>
            </div>
          </Card>

          <Card>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                <Award className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-ncaa-dark mb-3">Our Values</h3>
              <p className="text-gray-600">
                Safety First, Integrity, Professionalism, Accountability, and Continuous Improvement
                in all aspects of aviation regulation.
              </p>
            </div>
          </Card>
        </div>

        {/* Core Functions */}
        <Card className="mb-8">
          <h2 className="text-3xl font-bold text-ncaa-dark mb-6">Core Functions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Shield className="h-6 w-6 text-ncaa-primary" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Safety Regulation</h3>
                <p className="text-gray-600">
                  Developing and enforcing safety regulations, standards, and procedures for all
                  civil aviation operations in Nigeria.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Users className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Licensing & Certification
                </h3>
                <p className="text-gray-600">
                  Issuing licenses and certificates to aviation personnel, including pilots,
                  engineers, air traffic controllers, and other professionals.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Aircraft Certification</h3>
                <p className="text-gray-600">
                  Certifying aircraft for airworthiness and ensuring compliance with international
                  safety standards and regulations.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Globe className="h-6 w-6 text-orange-600" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  International Compliance
                </h3>
                <p className="text-gray-600">
                  Ensuring compliance with International Civil Aviation Organization (ICAO)
                  standards and recommended practices.
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* DCEV Portal Information */}
        <Card className="mb-8">
          <h2 className="text-3xl font-bold text-ncaa-dark mb-4">About DCEV Portal</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            The Digital Civil Aviation Enrollment (DCEV) Portal is NCAA's modern, user-friendly
            platform designed to streamline the aviation licensing and verification process. This
            portal enables:
          </p>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">
                <strong>Online Applications:</strong> Submit license applications from anywhere,
                anytime
              </span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">
                <strong>Document Management:</strong> Upload and manage required documents securely
              </span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">
                <strong>Appointment Booking:</strong> Schedule biometric capture and examinations
                online
              </span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">
                <strong>Real-time Tracking:</strong> Monitor your application status at every stage
              </span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">
                <strong>Transparency:</strong> Clear communication and updates throughout the
                process
              </span>
            </li>
          </ul>
        </Card>

        {/* Contact CTA */}
        <Card className="bg-gradient-to-r from-ncaa-primary to-blue-600 text-white">
          <div className="text-center py-8">
            <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-xl text-blue-100 mb-6">
              Begin your aviation career journey with NCAA's streamlined digital platform
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/register"
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-ncaa-primary font-semibold rounded-lg hover:bg-gray-100 transition-colors"
              >
                Create Account
              </a>
              <a
                href="/contact"
                className="inline-flex items-center justify-center px-6 py-3 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-ncaa-primary transition-colors"
              >
                Contact Support
              </a>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
